/* Avenger persistent storefront. Server HTML remains the source for every page. */
(() => {
  "use strict";
  if (window.Avenger) return;
  const config = JSON.parse(
    document.getElementById("AvengerConfig").textContent,
  );
  const root = config.root.endsWith("/") ? config.root : `${config.root}/`;
  const A = (window.Avenger = {
    config,
    root,
    duration(ms) {
      return ms * (config.motion || 1);
    },
    strings: config.strings,
    announce(message) {
      const node = document.querySelector("[data-av-status]");
      if (node) node.textContent = message;
    },
    money(cents) {
      return new Intl.NumberFormat(config.locale, {
        style: "currency",
        currency: config.currency,
      }).format(cents / 100);
    },
    async request(url, options = {}) {
      const { allow404 = false, ...fetchOptions } = options;
      const response = await fetch(url, {
        credentials: "same-origin",
        ...fetchOptions,
      });
      if (!response.ok && !(allow404 && response.status === 404)) {
        const error = new Error(A.strings.error);
        try {
          const body = await response.json();
          error.message = body.description || body.message || error.message;
        } catch (_) {
          /* Non-JSON errors use translated fallback. */
        }
        error.status = response.status;
        throw error;
      }
      return response;
    },
    json(url, data, signal) {
      return A.request(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
        signal,
      }).then((r) => r.json());
    },
    closePanels(immediate = false) {
      document.dispatchEvent(new Event("av:overlays-close"));
      document
        .querySelectorAll("av-panel")
        .forEach((p) => p.close?.(immediate));
      document.querySelector("avenger-header")?.closeAll?.(immediate);
    },
  });
  class Panel extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.dialog = this.querySelector("dialog");
      this.addEventListener(
        "click",
        (e) => {
          if (e.target.closest("[data-panel-close]")) this.close();
          if (e.target === this.dialog) {
            const r = this.dialog.getBoundingClientRect();
            if (
              e.clientX < r.left ||
              e.clientX > r.right ||
              e.clientY < r.top ||
              e.clientY > r.bottom
            )
              this.close();
          }
        },
        { signal },
      );
      this.dialog.addEventListener(
        "cancel",
        (e) => {
          e.preventDefault();
          this.close();
        },
        { signal },
      );
      this.dialog.addEventListener(
        "close",
        () => {
          this.trigger?.setAttribute("aria-expanded", "false");
        },
        { signal },
      );
      window.addEventListener("resize", () => this.position(), { signal });
      window.addEventListener("scroll", () => this.position(), {
        passive: true,
        signal,
      });
      this.resize = new ResizeObserver(() => this.position());
      this.resize.observe(this.dialog);
      document.addEventListener(
        "click",
        (event) => {
          if (
            this.nonmodal &&
            this.dialog.open &&
            !event.composedPath().includes(this) &&
            !this.trigger?.contains(event.target)
          )
            this.close(false, false);
        },
        { signal },
      );
      document.addEventListener(
        "keydown",
        (event) => {
          if (this.nonmodal && this.dialog.open && event.key === "Escape") {
            event.preventDefault();
            this.close();
          }
        },
        { signal },
      );
      this.addEventListener(
        "focusout",
        (event) => {
          if (
            this.nonmodal &&
            this.dialog.open &&
            event.relatedTarget &&
            !this.contains(event.relatedTarget) &&
            !this.trigger?.contains(event.relatedTarget)
          )
            this.close(false, false);
        },
        { signal },
      );
    }
    open(trigger) {
      if (this.dialog.open && this.trigger === trigger) {
        this.close();
        return;
      }
      A.closePanels(true);
      clearTimeout(this.timer);
      this.trigger = trigger || document.activeElement;
      this.dialog.classList.remove("is-closing");
      this.nonmodal = ["dropdown", "top"].includes(this.dataset.style);
      this.dialog.toggleAttribute("scroll-lock", !this.nonmodal);
      if (!this.dialog.open) {
        if (this.nonmodal) this.dialog.show();
        else this.dialog.showModal();
      }
      this.position();
      this.trigger?.setAttribute("aria-expanded", "true");
      this.dialog
        .querySelector("input, button, a")
        ?.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent("av:panel-open", { bubbles: true }));
    }
    position() {
      if (
        this.dataset.style !== "dropdown" ||
        !this.dialog.open ||
        !this.trigger?.isConnected
      )
        return;
      const r = this.trigger.getBoundingClientRect();
      const width = Math.min(
        Number(this.dataset.width) || 440,
        innerWidth - 24,
      );
      this.dialog.style.setProperty("--panel-width", `${width}px`);
      this.dialog.style.setProperty(
        "--panel-left",
        `${Math.max(12, Math.min(r.right - width, innerWidth - width - 12))}px`,
      );
      this.dialog.style.setProperty(
        "--panel-top",
        `${Math.max(12, Math.min(r.bottom + 12, innerHeight - 120))}px`,
      );
      this.dialog.style.setProperty(
        "--panel-max",
        `${Math.max(100, innerHeight - Math.max(12, Math.min(r.bottom + 12, innerHeight - 120)) - 12)}px`,
      );
    }
    close(immediate = false, restoreFocus = true) {
      if (!this.dialog?.open) return;
      clearTimeout(this.timer);
      this.dialog.classList.add("is-closing");
      const finish = () => {
        this.dialog.close();
        this.dialog.classList.remove("is-closing");
        this.dispatchEvent(
          new CustomEvent("av:panel-close", { bubbles: true }),
        );
        if (restoreFocus && this.trigger?.isConnected)
          this.trigger.focus({ preventScroll: true });
      };
      if (immediate) finish();
      else this.timer = setTimeout(finish, A.duration(180));
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      clearTimeout(this.timer);
      this.dialog?.close();
    }
  }
  customElements.define("av-panel", Panel);
  document.addEventListener("click", (e) => {
    const trigger = e.target.closest("[data-panel-open]");
    if (!trigger) return;
    const panel = document.getElementById(trigger.dataset.panelOpen);
    if (!panel?.open) return;
    e.preventDefault();
    panel.open(trigger);
  });
  if (!config.instant || window.Shopify?.designMode) return;
  const cache = new Map(),
    pending = new Map();
  A.invalidate = () => cache.clear();
  let sequence = 0,
    controller;
  history.scrollRestoration = "manual";
  history.replaceState(
    { ...history.state, av: true, scroll: [scrollX, scrollY] },
    "",
    location.href,
  );
  let scrollTimer;
  addEventListener(
    "scroll",
    () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(
        () =>
          history.replaceState(
            { ...history.state, av: true, scroll: [scrollX, scrollY] },
            "",
            location.href,
          ),
        100,
      );
    },
    { passive: true },
  );
  function eligible(url) {
    const u = new URL(url, location.href);
    return (
      ["http:", "https:"].includes(u.protocol) &&
      u.origin === location.origin &&
      !/\/(?:account|checkout|checkouts|apps|admin|challenge|services|localization|cart\/(?:add|change|update|clear))(?:\/|$)/.test(
        u.pathname,
      ) &&
      !/\.(?:pdf|zip|png|jpe?g|webp|svg|mp4)$/i.test(u.pathname)
    );
  }
  function safeDocument(doc) {
    if (
      doc.documentElement.dataset.avenger !== "2" ||
      !doc.querySelector("[data-av-main]")
    )
      return false;
    // A destination with executable app markup gets ordinary navigation. Never eval fetched scripts.
    return ![
      ...doc.querySelector("[data-av-main]").querySelectorAll("script"),
    ].some(
      (s) => !["application/json", "application/ld+json"].includes(s.type),
    );
  }
  async function load(url, signal) {
    const key = new URL(url, location.href);
    key.hash = "";
    const hit = cache.get(key.href);
    if (
      hit &&
      Date.now() - hit.time < 20000 &&
      !/\/cart(?:\/|$)/.test(key.pathname)
    )
      return hit.html;
    const response = await A.request(key.href, {
      signal,
      headers: { "X-Avenger-Navigation": "1" },
      allow404: true,
    });
    if (
      new URL(response.url).origin !== location.origin ||
      new URL(response.url).pathname !== key.pathname
    )
      throw new Error("Redirect requires full navigation");
    const html = await response.text();
    cache.set(key.href, { html, time: Date.now() });
    if (cache.size > 10) cache.delete(cache.keys().next().value);
    return html;
  }
  A.navigate = async (
    url,
    {
      pop = false,
      scroll = null,
      focus = true,
      searchLive = false,
      replaceHistory = false,
    } = {},
  ) => {
    const target = new URL(url, location.href),
      ticket = ++sequence;
    controller?.abort();
    controller = new AbortController();
    document.documentElement.classList.add("av-navigating");
    A.announce(A.strings.loading);
    try {
      const html = await load(target.href, controller.signal);
      const doc = new DOMParser().parseFromString(html, "text/html");
      if (
        [...doc.head.querySelectorAll("script[src]")].some(
          (s) =>
            ![...document.head.querySelectorAll("script[src]")].some(
              (loaded) => loaded.src === s.src,
            ),
        )
      )
        throw new Error("Integration requires full navigation");
      if (!safeDocument(doc)) throw new Error("Page requires full navigation");
      if (ticket !== sequence) return;
      if (
        searchLive &&
        document
          .querySelector("av-search-page input[name='q']")
          ?.value.trim() !== (target.searchParams.get("q") || "")
      )
        return;
      A.closePanels(true);
      const commit = () => {
        const main = document.querySelector("[data-av-main]");
        const currentResults = main.querySelector(
          "[data-search-results-region]",
        );
        const nextResults = doc.querySelector("[data-search-results-region]");
        if (
          searchLive &&
          currentResults &&
          nextResults &&
          target.pathname === location.pathname
        )
          currentResults.replaceWith(document.importNode(nextResults, true));
        else
          main.replaceWith(
            document.importNode(doc.querySelector("[data-av-main]"), true),
          );
        document.title = doc.title;
        document.head
          .querySelectorAll("[data-av-meta]:not(title)")
          .forEach((n) => n.remove());
        doc.head
          .querySelectorAll("[data-av-meta]:not(title)")
          .forEach((n) => document.head.append(document.importNode(n, true)));
        // Shopify emits section CSS links per page; retain loaded styles and load new ones once.
        doc.querySelectorAll('link[rel="stylesheet"]').forEach((n) => {
          if (
            ![...document.querySelectorAll('link[rel="stylesheet"]')].some(
              (x) => x.href === n.href,
            )
          )
            document.head.append(document.importNode(n, true));
        });
        document.body.dataset.pageType = doc.body.dataset.pageType;
        if (!pop) {
          history.replaceState(
            { ...history.state, av: true, scroll: [scrollX, scrollY] },
            "",
            location.href,
          );
          if (replaceHistory)
            history.replaceState(
              { av: true, scroll: scroll || [scrollX, scrollY] },
              "",
              target.href,
            );
          else history.pushState({ av: true, scroll: [0, 0] }, "", target.href);
        }
        document.querySelectorAll("avenger-header a[href]").forEach((a) => {
          const active = new URL(a.href).pathname === target.pathname;
          if (active) a.setAttribute("aria-current", "page");
          else a.removeAttribute("aria-current");
        });

        if (focus)
          document
            .querySelector("[data-av-main]")
            .focus({ preventScroll: true });
        if (scroll) window.scrollTo(...scroll);
        else if (target.hash) {
          try {
            document
              .getElementById(decodeURIComponent(target.hash.slice(1)))
              ?.scrollIntoView();
          } catch (_) {
            window.scrollTo(0, 0);
          }
        } else window.scrollTo(0, 0);
        document.dispatchEvent(
          new CustomEvent("av:page", { detail: { url: target.href } }),
        );
        if (!searchLive)
          document.querySelector("[data-av-main]")?.animate?.(
            [
              { opacity: 0.94, transform: "translateY(4px)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            { duration: A.duration(180), easing: "cubic-bezier(.22,1,.36,1)" },
          );
        A.announce(doc.title);
      };
      commit();
    } catch (error) {
      if (error.name !== "AbortError" && ticket === sequence)
        location.assign(target.href);
    } finally {
      if (ticket === sequence)
        document.documentElement.classList.remove("av-navigating");
    }
  };
  document.addEventListener("click", (e) => {
    const a = e.target.closest("a[href]");
    if (
      !a ||
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      a.target ||
      a.hasAttribute("download") ||
      a.hasAttribute("data-full-navigation") ||
      a.closest("shopify-account") ||
      !eligible(a.href)
    )
      return;
    const u = new URL(a.href);
    if (
      u.pathname === location.pathname &&
      u.search === location.search &&
      u.hash
    )
      return;
    e.preventDefault();
    A.navigate(
      u.href,
      a.closest(".av-active-filters") ? { scroll: [scrollX, scrollY] } : {},
    );
  });
  document.addEventListener("submit", (e) => {
    const form = e.target;
    if (
      e.defaultPrevented ||
      form.method.toLowerCase() !== "get" ||
      form.hasAttribute("data-full-navigation") ||
      !eligible(form.action)
    )
      return;
    const url = new URL(form.action);
    url.search = new URLSearchParams(new FormData(form)).toString();
    e.preventDefault();
    const filters =
      form.hasAttribute("data-filter-form") ||
      !!form.querySelector("[data-sort]");
    const control = form.contains(document.activeElement)
      ? document.activeElement
      : null;
    const identity = control && {
      name: control.name,
      value: control.value,
      label: control.getAttribute("aria-label"),
    };
    A.navigate(url.href, filters ? { scroll: [scrollX, scrollY] } : {}).then(
      () => {
        if (!filters || !identity || location.href !== url.href) return;
        const candidates = document.querySelectorAll(
          "[data-filter-form] input, [data-sort]",
        );
        const next = [...candidates].find((input) =>
          identity.name
            ? input.name === identity.name &&
              (input.type !== "checkbox" || input.value === identity.value)
            : input.getAttribute("aria-label") === identity.label,
        );
        if (next) {
          const selectButton = next
            .closest("av-select")
            ?.querySelector(".av-select-button");
          (selectButton || next).focus({ preventScroll: true });
        }
      },
    );
  });
  addEventListener("popstate", (e) => {
    if (e.state?.av)
      A.navigate(location.href, {
        pop: true,
        scroll: e.state.scroll || [0, 0],
      });
    else location.reload();
  });
  document.addEventListener(
    "pointerover",
    (e) => {
      if (
        pending.size >= 3 ||
        navigator.connection?.saveData ||
        navigator.connection?.effectiveType?.includes("2g")
      )
        return;
      const a = e.target.closest("a[href]");
      if (!a || a.dataset.panelOpen || !eligible(a.href)) return;
      const url = new URL(a.href);
      if (
        /\/cart(?:\/|$)/.test(url.pathname) ||
        url.pathname === location.pathname ||
        pending.has(url.href)
      )
        return;
      pending.set(url.href, true);
      load(url.href)
        .catch(() => {})
        .finally(() => {
          pending.delete(url.href);
        });
    },
    { passive: true },
  );
})();
